<template>
    <div id="video-container">
        <div
            v-show="!locked && !loading && !playbackSessionLimitExceeded"
            id="video-player-mount-container"
            class="vid-container"
            :style="{
                'background-image': 'url(&quot;' + poster + '&quot;)',
            }"
        ></div>
        <div
            v-if="locked || loading || playbackSessionLimitExceeded"
            id="vid-preview"
            :style="{
                'background-image': 'url(&quot;' + poster + '&quot;)',
            }"
        >
            <div v-if="loading" id="video-loading">
                <md-progress-spinner
                    md-mode="indeterminate"
                ></md-progress-spinner>
            </div>
            <div v-else-if="notFound" id="video-not-found">
                <md-empty-state
                    class="md-accent"
                    md-rounded
                    md-icon="error"
                    md-label="Video Not Found"
                    md-description="We could not load this video. It may have been removed."
                >
                    <div>
                        <md-button
                            class="md-primary md-raised"
                            @click="$router.go(-1)"
                            >Go Back</md-button
                        >
                    </div>
                </md-empty-state>
            </div>
            <div
                v-else-if="playbackSessionLimitExceeded"
                id="playback-session-limit"
            >
                <md-empty-state
                    class="md-accent"
                    md-rounded
                    md-icon="error"
                    md-label="Video Playback Session Limit Exceeded"
                    md-description="There are too many people watching videos on your account!"
                >
                    <div>
                        <md-button
                            class="md-raised"
                            @click="playbackSessionLimitExceeded = false"
                            >OK</md-button
                        >
                    </div>
                </md-empty-state>
            </div>
            <router-link
                v-else
                class="playbutton large-btn"
                :title="subscribeTitleText"
                to="/subscribe"
            >
                <md-icon class="md-size-2x subscriber-lock-button"
                    >lock</md-icon
                >
                <h2 class="subscribe-lock-text">{{ subscribeText }}</h2>
            </router-link>
        </div>
    </div>
</template>

<script>
import size from 'lodash/size';

export default {
    name: 'VideoPlayer',
    props: {
        id: {
            type: String,
            default: '',
        },
        groupId: {
            type: String,
            default: '',
        },
        poster: {
            type: String,
            default: '',
        },
        settings: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        authed: false,
        player: undefined,
        playbackRateOptions: [],
        vastUrl: undefined,
        analyticsBaseUrl: undefined,
        locked: true,
        loading: true,
        subscribeTitleText: 'Subscribe To Watch',
        subscribeText: '',
        playbackSessionId: undefined,
        hasPlaybackSessionLimit: false,
        playbackSessionLastChecked: Date.now(),
        bookmarkLastSaved: Date.now(),
        playbackSessionLimitExceeded: false,
        playNext: {},
    }),
    computed: {
        mediaQuery: function () {
            return this.$mq;
        },
        shouldTrackPlaybackSession: function () {
            return (
                this.hasPlaybackSessionLimit &&
                this.settings?.freeBehavior === 'disallow'
            );
        },
    },
    watch: {
        id: function (newVal, oldVal) {
            this.loadVideo().then(() => {
                try {
                    if (oldVal) {
                        //if this is not on a page load
                        this.player.play();
                    }
                } catch (e) {
                    //eslint-disable-next-line
                    console.error(e);
                }
            });
        },
        mediaQuery: function () {
            this.setAspectRatio();
        },
    },
    async mounted() {
        await this.loadVideo();
    },
    beforeDestroy() {
        void this.deletePlaybackSession();
    },
    async created() {
        const settings = await this.$appSettings;
        const setAuthed = () => {
            this.authed = this.$gideo.authed;
        };
        this.$gideo.bus.$on('logged-in', setAuthed);
        this.$gideo.bus.$on('logged-out', setAuthed);
        setAuthed();

        this.hasPlaybackSessionLimit = settings.hasPlaybackSessionLimit;
        this.playbackRateOptions = settings.changePlaybackRate
            ? [0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4]
            : [];
        this.vastUrl = settings.vastUrl;
        this.analyticsBaseUrl = settings.apiBaseUrls.analytics;

        this.subscribeTitleText = settings.hasSubscriptions
            ? settings.signupButtonText || 'Subscribe To Watch'
            : settings.signupButtonText || 'Signup To Watch';

        this.subscribeText = settings.signupButtonText;
    },
    methods: {
        setAspectRatio() {
            if (!this.player) {
                return;
            }
            if (this.$mq === 'xs') {
                this.player.setAspectRatio(undefined);
            } else {
                this.player.setAspectRatio('16:9');
            }
        },
        mountPlayer() {
            const mountPoint = global.document.createElement('div');
            mountPoint.setAttribute('id', 'video-player-mount');
            this.$el
                .querySelector('#video-player-mount-container')
                .appendChild(mountPoint);
            this.player = new global.StreamotorPlayer(mountPoint);
        },
        async loadVideo() {
            if (!this.id) {
                return;
            }
            const playerWidth = this.player?.getWidth();
            const playerHeight = this.player?.getHeight();
            const fullScreen = this.player?.isFullscreen();
            if (this.player) {
                this.unmountPlayer();
            }
            this.notFound = false;
            this.locked = false;
            this.loading = true;

            let files = [];
            let postroll, preroll, adSupported, adCuePoints;
            let bookmark = 0;

            try {
                this.playNext = await this.$gideo.getNextVideo(
                    this.id,
                    this.groupId,
                );
                files = await this.$gideo.getProgramURLs(this.id);
                bookmark = files.data.bookmark;
                preroll = files.data.preroll;
                postroll = files.data.postroll;
                adSupported = files.data.adSupported;
                adCuePoints = files.data.adCuePoints;
                files = files.data.urls;
                this.locked = false;
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.notFound = true;
                } else if (
                    e.response &&
                    (e.response.status === 403 || e.response.status === 401)
                ) {
                    this.locked = true;
                } else {
                    this.locked = true;
                    //console.error(e);
                }
                this.loading = false;
                return;
            }
            this.mountPlayer();
            if (playerHeight && playerWidth) {
                this.player.setHeight(playerHeight);
                this.player.setWidth(playerWidth);
            }
            this.setupPlaybackSession();
            const gideoAnalytics = size(this.analyticsBaseUrl)
                ? { endpoint: this.analyticsBaseUrl }
                : undefined;

            //setup for live streams
            if (this.settings?.live) {
                this.player.setup({
                    gideoAnalytics,
                    autoplay: !!this.settings.playImmediately,
                    smlive: files[0].url,
                });
                //setup for scheduled playout
            } else if (this.settings && this.settings.scheduleId) {
                this.player.setup({
                    gideoAnalytics,
                    autoplay: !!this.settings.playImmediately,
                    smLivePlayoutScheduleID: this.settings.scheduleId,
                });
            } else if (this.id) {
                const prerollSetup =
                    preroll && !bookmark
                        ? {
                              skip: preroll.skip,
                              video: {
                                  files: [{ http: preroll.urls[0].url }],
                              },
                              text: preroll.text,
                              url: preroll.link,
                          }
                        : null;
                const postrollSetup = postroll
                    ? {
                          video: {
                              files: [{ http: postroll.urls[0].url }],
                          },
                          text: postroll.text,
                          url: postroll.link,
                      }
                    : null;
                const vast =
                    this.vastUrl && adSupported
                        ? {
                              url: this.vastUrl,
                              cues: adCuePoints,
                          }
                        : undefined;
                const setup = {
                    playbackRates: [...this.playbackRateOptions],
                    gideoAnalytics,
                    muted: !!preroll,
                    smpreroll: prerollSetup,
                    smpostroll: postrollSetup,
                    vast,
                    smvideo: {
                        videoid: this.id,
                        captions: this.settings.subtitleTracks,
                        autoplay: !!this.settings.playImmediately, // boolean
                        muted: false, // boolean
                        poster: this.poster, // url
                        title: this.settings.title,
                        overlayText: this.settings.overlayText,
                        thumbnail: '', // url
                        logo: this.settings.logo, // url
                        logoUrl: this.settings.logoUrl, // url
                        logoPosition: this.settings.logoPosition, // top-left | bottom-left | top-right | bottom-right
                        controlBar: null, // boolean
                        related: null, // array
                        relatedHeading: '',
                        sharing: null, // object
                        files: [
                            {
                                http: files[0].url, // url
                                width: 0, // integer
                            },
                        ],
                    },
                };

                this.player.setup(setup);

                if (bookmark) {
                    this.player.seek(bookmark);
                }
            }

            this.loading = false;
            this.setAspectRatio();
            if (fullScreen) {
                this.player.requestFullscreen();
            }
        },
        setupPlaybackSession() {
            this.player.addEventListener('ended', async () => {
                if (this.player.isPaused() && this.playNext?.videoId) {
                    this.$router.push(
                        `/program-group/${this.playNext.containerId || ''}/program/${this.playNext.videoId}`,
                    );
                }
                return;
            });
            //don't track playback sessions for accounts without a limit or for free videos
            if (this.shouldTrackPlaybackSession) {
                this.player.addEventListener('play', async () => {
                    await this.createPlaybackSession();
                });
            } else if (this.settings?.live || !this.authed) {
                //nothing to do if not tracking session and video is live or user is not logged in
                return;
            }

            this.player.addEventListener('timeupdate', async () => {
                if (this.shouldTrackPlaybackSession) {
                    await this.checkPlaybackSession();
                }
                if (this.authed && !this.settings?.live) {
                    await this.saveBookmark(false);
                }
            });
            this.player.addEventListener('pause', async () => {
                if (this.shouldTrackPlaybackSession) {
                    await this.deletePlaybackSession();
                }
                if (this.authed && !this.settings?.live) {
                    await this.saveBookmark(true);
                }
            });
        },
        async createPlaybackSession() {
            //create a playback session only if one has not yet been created
            if (this.playbackSessionId) {
                return;
            }

            try {
                this.playbackSessionId =
                    await this.$gideo.createPlaybackSession(this.id);
                this.playbackSessionLastChecked = Date.now();
            } catch (e) {
                if (e.status === 403 || e.status === 401) {
                    this.playbackSessionId = undefined;
                    this.player.pause();
                    this.locked = true;
                } else if (e.status >= 500) {
                    return;
                }
            }
        },
        async saveBookmark(force = false) {
            try {
                const now = Date.now();
                //only add bookmark every 20 seconds
                if (now - this.bookmarkLastSaved < 20000 && !force) {
                    return;
                }

                this.bookmarkLastSaved = now;

                const bookmark = {
                    position: this.player.currentTime(),
                    watched: false,
                    videoId: this.id,
                };
                const duration =
                    this.settings.duration || this.player.realDuration();

                if (this.player.currentTime() < 45) {
                    bookmark.position = 0;
                } else if (
                    Number.isFinite(duration) &&
                    duration - this.player.currentTime() < 45
                ) {
                    bookmark.position = 0;
                    bookmark.watched = true;
                }

                await this.$gideo.saveBookmark(bookmark);
                return;
            } catch (e) {
                //eslint-disable-next-line
                console.error(e);
                //do nothing on error
                return;
            }
        },
        async checkPlaybackSession() {
            //check a playback session only if one has been created
            if (!this.playbackSessionId) {
                return;
            }
            const now = Date.now();
            //only check every 10 seconds
            if (now - this.playbackSessionLastChecked < 10000) {
                return;
            }

            this.playbackSessionLastChecked = now;
            try {
                await this.$gideo.checkPlaybackSession(this.playbackSessionId);
            } catch (e) {
                if (e.status === 403 || e.status === 401) {
                    this.player.pause();
                    this.locked = true;
                } else {
                    this.player.pause();
                    this.playbackSessionLimitExceeded = true;
                }
                return;
            }
        },
        async deletePlaybackSession() {
            //delete a playback session only if one has been created
            if (!this.playbackSessionId) {
                return;
            }
            try {
                await this.$gideo.deletePlaybackSession(this.playbackSessionId);
            } catch (e) {
                //do nothing on an error
            } finally {
                this.playbackSessionId = undefined;
            }
        },
        unmountPlayer() {
            const mountPoint = this.$el.querySelector('#video-player-mount');
            if (mountPoint) {
                mountPoint.remove();
            }
            this.player = null;
            this.deletePlaybackSession();
        },
    },
};
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '@/assets/css/_variables.scss';
#vid-preview {
    width: 100%;
    height: 0;
    padding-bottom: math.div(1080, 1920) * 100%;
    background-position: center center;
    background-size: contain;
    background-repeat: no-repeat;

    .playbutton,
    #video-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        color: #fff;
        &:hover {
            color: #448aff;
        }
    }

    &::before {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: rgba(#000, 0.5);
    }
}
#video-container {
    background-color: #000;
    position: relative;
    min-height: 15rem;

    @media (min-width: 1280px) {
        min-height: 25rem;
    }
}
#video-not-found {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    color: #fff;
}
#playback-session-limit {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    color: #fff;
    background-color: #000000bb;
}
.subscribe-lock-text {
    position: absolute;
    margin: 0;
    padding: 0.5em;
    left: 50%;
    bottom: 0;
    display: block;
    translate: -50% 100%;
    white-space: nowrap;
}
</style>
